import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Send, Sparkles, X, Plus, MessageSquare, Trash2, Loader2,
  CheckCircle2, AlertCircle, ExternalLink, User as UserIcon,
  FileText, FileSignature, Calendar as CalendarIcon, ListChecks, DollarSign,
  Zap, Bot, HelpCircle, ArrowLeft, Eye, Check, Pencil, Mic, Square, Wand2, Undo2, Briefcase,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { errMsg } from '../lib/apiError';
import { runAiJob } from '../lib/aiJob';
import useAiPrefs from '../hooks/useAiPrefs';
import { useAiContext } from '../contexts/AiContext';

/**
 * Ask Mantle — the global AI agent panel.
 *
 * Two modes:
 *  - "Agent" (default): POST /api/ai/agent — the AI can TAKE actions
 *    (create clients, estimates, invoices, contracts, expenses, events,
 *    tasks). Returns reply + action cards with deeplinks.
 *  - "Chat":  POST /api/ai/chat (SSE)  — pure conversational fallback
 *    for general questions ("how much did I earn last month?").
 *
 * The panel is mounted once in AppShell so the conversation survives
 * route changes. It posts the current page slug as `page_context` so
 * the agent can disambiguate "add Monday" on /schedule vs /tasks.
 */

const ACTION_ICONS = {
  client: UserIcon,
  estimate: FileText,
  invoice: FileText,
  contract: FileSignature,
  expense: DollarSign,
  event: CalendarIcon,
  task: ListChecks,
  edit: Wand2,
  undo: Undo2,
};

const CONTEXT_ICONS = {
  invoice: FileText,
  estimate: FileText,
  document: FileSignature,
  client: UserIcon,
  project: Briefcase,
};

// Suggestions shown when the user has a document open — these drive the
// edit_* tools instead of creating new records.
const CONTEXT_SUGGESTIONS = {
  invoice: [
    'Make the description for line item 1 more professional',
    'Add a $500 deposit',
    'Change the payment terms to 40% deposit and 60% upon completion',
    'Add another line item for floor preparation',
    'Undo that last change',
  ],
  estimate: [
    'Make the description for line item 1 more professional',
    'Add a $500 deposit',
    'Change the payment terms to 40% deposit and 60% upon completion',
    'Add another line item for floor preparation',
    'Undo that last change',
  ],
  document: [
    'Make this contract shorter',
    'Make the payment terms section more formal',
    'Change the layout, but keep all of the existing information',
    'Add a 12-month workmanship warranty clause',
    'Undo that last change',
  ],
};

const AGENT_SUGGESTIONS = [
  'Add a client named Mike Daly, 555-0145',
  'Make me a flooring estimate for 600 sqft @ $4.75',
  'Make me a flooring contract for my last client',
  'Add a $120 gas expense from yesterday',
  'Schedule a quote with Mike on Monday 10am',
  'Remind me to call the tile supplier tomorrow',
];

const CHAT_SUGGESTIONS = [
  'What invoices are overdue?',
  'Draft a polite follow-up for an unpaid invoice',
  'How much did I earn last month?',
  'Summarize my upcoming week',
];

// "What can Ask Mantle do?" — rendered inside the help drawer. Each entry
// maps a tool name (from /api/ai/agent/tools) to an icon, one-line summary,
// and a couple of tap-to-fill example prompts so users learn by doing.
const TOOL_HELP = [
  {
    tool: 'create_client', icon: UserIcon, label: 'Add a Client',
    summary: 'Adds someone to your contacts directory.',
    examples: [
      'Add a client named Mike Daly, phone 555-0145',
      'Add ACME Roofing as a client, email john@acme.com',
    ],
  },
  {
    tool: 'create_estimate', icon: FileText, label: 'Draft an Estimate',
    summary: 'Builds a draft estimate with realistic line items.',
    examples: [
      'Make me a flooring estimate for 500 sqft of vinyl plank at $5.50/sqft',
      'Estimate for Mike: 3 doorways + transition strips',
    ],
  },
  {
    tool: 'create_invoice', icon: FileText, label: 'Draft an Invoice',
    summary: 'Same as estimate but flagged for collection.',
    examples: [
      'Invoice ACME for 8 hours of consulting at $125/hr',
      'Make me an invoice for the Daly bathroom job — $3,200',
    ],
  },
  {
    tool: 'create_contract', icon: FileSignature, label: 'Draft a Contract',
    summary: 'Generates a service contract with sections you can edit.',
    examples: [
      'Make me a flooring contract for Mike Daly',
      'Draft a remodel contract for the Smith kitchen project',
    ],
  },
  {
    tool: 'add_expense', icon: DollarSign, label: 'Log an Expense',
    summary: 'Records a transaction in Bookkeeping.',
    examples: [
      'Add a $120 gas expense from yesterday',
      'Log $48 at Home Depot for screws',
    ],
  },
  {
    tool: 'schedule_event', icon: CalendarIcon, label: 'Schedule an Event',
    summary: 'Drops a job, quote-visit, or meeting on your calendar.',
    examples: [
      'Schedule a quote with Mike on Monday at 10am',
      'Book the Smith install for next Tuesday all day',
    ],
  },
  {
    tool: 'add_task', icon: ListChecks, label: 'Add a Task',
    summary: 'Adds a to-do to your task list.',
    examples: [
      'Remind me to call the tile supplier tomorrow',
      'Add a task: order baseboards before Friday',
    ],
  },
];

function pageSlugFromPath(pathname) {
  if (!pathname) return null;
  const seg = pathname.replace(/^\/app\/?/, '').split('/')[0] || 'dashboard';
  return seg || null;
}

export default function AskMantlePanel({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { enabled } = useAiPrefs();
  const aiCtx = useAiContext();
  const context = aiCtx.context;
  const contextKey = context ? `${context.type}:${context.id}` : 'global';

  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]); // [{role, content, actions?}]
  // Each open document keeps its own conversation so switching between an
  // invoice and a contract doesn't mix threads. Keyed by contextKey.
  const threadsRef = useRef({});
  const prevKeyRef = useRef(contextKey);
  useEffect(() => {
    if (prevKeyRef.current === contextKey) return;
    threadsRef.current[prevKeyRef.current] = { session: activeSession, messages };
    const next = threadsRef.current[contextKey] || { session: null, messages: [] };
    setActiveSession(next.session);
    setMessages(next.messages);
    prevKeyRef.current = contextKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [slow, setSlow] = useState(false); // >15s in flight → reassure the user
  const [streamingText, setStreamingText] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // mode = 'agent' (default) or 'chat' (no actions). Persisted per session.
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'agent';
    return localStorage.getItem('mantle_ai_mode') || 'agent';
  });
  // Preview mode = dry-run. AI shows what it WOULD do; user confirms.
  // Defaults to OFF so behavior is unchanged for existing users. When
  // on, every agent action is returned as a preview card with
  // Confirm / Edit / Cancel buttons — safe for live client demos.
  const [previewMode, setPreviewMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('mantle_ai_preview') === '1';
  });
  const [confirmingIdx, setConfirmingIdx] = useState(null); // "msgIdx-actIdx"
  // Voice input — MediaRecorder state. `recording` flips the mic button
  // to a Stop icon; `transcribing` shows a spinner while Whisper runs.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-grow the composer textarea up to ~8 lines. Resetting height to
  // 'auto' first lets it shrink when the user deletes text.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 220); // 220px ≈ 8-9 lines
    el.style.height = `${next}px`;
  }, [input]);

  useEffect(() => { localStorage.setItem('mantle_ai_mode', mode); }, [mode]);
  useEffect(() => { localStorage.setItem('mantle_ai_preview', previewMode ? '1' : '0'); }, [previewMode]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingText]);

  useEffect(() => {
    if (!open) return;
    api.get('/ai/sessions').then(r => setSessions(r.data || [])).catch(() => {});
  }, [open]);

  const loadSession = async (sessionId) => {
    setActiveSession(sessionId);
    setShowHistory(false);
    try {
      const { data } = await api.get(`/ai/sessions/${sessionId}`);
      setMessages(data || []);
    } catch { setMessages([]); }
  };

  const newChat = () => {
    setActiveSession(null);
    setMessages([]);
    setInput('');
    setShowHistory(false);
  };

  const deleteSession = async (sessionId, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await api.delete(`/ai/sessions/${sessionId}`);
      setSessions(s => s.filter(x => x.session_id !== sessionId));
      if (activeSession === sessionId) newChat();
    } catch { toast.error('Could not delete.'); }
  };

  // Apply an edit/undo patch to whatever editor is registered as context.
  const applyLocal = (action) => {
    if (!action?.client_apply || !action.ok) return false;
    try {
      if (action.patch?.undo) aiCtx.undo();
      else aiCtx.applyPatch(action.patch);
      return true;
    } catch (e) {
      toast.error('Could not apply that change in the editor.');
      return false;
    }
  };

  // ---- AGENT MODE -----------------------------------------------------
  const sendAgent = async (text) => {
    try {
      const slug = pageSlugFromPath(location.pathname);
      const payload = {
        session_id: activeSession,
        message: text,
        page_context: slug,
        dry_run: previewMode,
      };
      if (context) {
        payload.context = {
          type: context.type,
          id: context.id,
          label: context.label,
          editable: !!context.editable,
          snapshot: aiCtx.getSnapshot() || {},
        };
      }
      const data = await runAiJob('/ai/agent', payload);
      if (data.session_id) setActiveSession(data.session_id);
      const actions = (data.actions || []).map(a => {
        if (a.client_apply && a.ok && !a.preview) {
          return { ...a, applied: applyLocal(a) };
        }
        return a;
      });
      setMessages(m => [...m, {
        role: 'assistant',
        content: data.reply || '',
        actions,
        userMessage: text,
      }]);
      // Surface a success toast for actions that created something.
      // (Skip in preview mode — nothing was actually created yet.)
      if (!previewMode) {
        const created = actions.filter(a => a.ok && a.created);
        const applied = actions.filter(a => a.applied);
        if (created.length === 1) toast.success(created[0].message || 'Done.');
        else if (created.length > 1) toast.success(`${created.length} actions completed.`);
        else if (applied.length) toast.success(applied[0].message || 'Change applied in the editor.');
      }
      api.get('/ai/sessions').then(r => setSessions(r.data || [])).catch(() => {});
    } catch (e) {
      const msg = errMsg(e, 'AI is unavailable right now.');
      toast.error(msg);
      setMessages(m => [...m, { role: 'assistant', content: `Sorry — ${msg}`, actions: [] }]);
    }
  };

  // Confirm a previewed action → executes it via /ai/agent/confirm and
  // swaps the preview card in-place with the real result.
  const confirmAction = async (msgIdx, actIdx) => {
    const action = messages[msgIdx]?.actions?.[actIdx];
    if (!action || !action.preview) return;
    const key = `${msgIdx}-${actIdx}`;
    // Edit/undo previews never touch the backend — apply in the editor.
    if (action.client_apply) {
      const ok = applyLocal({ ...action, preview: false });
      setMessages(m => m.map((mm, i) => i !== msgIdx ? mm : {
        ...mm,
        actions: mm.actions.map((aa, j) => j !== actIdx ? aa : { ...aa, preview: false, applied: ok }),
      }));
      if (ok) toast.success(action.message || 'Change applied in the editor.');
      return;
    }
    setConfirmingIdx(key);
    try {
      const { data } = await api.post('/ai/agent/confirm', {
        session_id: activeSession,
        tool: action.tool,
        params: action.params || {},
      });
      setMessages(m => m.map((mm, i) => i !== msgIdx ? mm : {
        ...mm,
        actions: mm.actions.map((aa, j) => j !== actIdx ? aa : { ...data }),
      }));
      if (data.ok && data.created) toast.success(data.message || 'Done.');
      else if (!data.ok) toast.error(data.message || 'Action failed.');
    } catch (e) {
      toast.error(errMsg(e, 'Could not run that action.'));
    } finally {
      setConfirmingIdx(null);
    }
  };

  // Cancel a previewed action — just removes it from the card list.
  const cancelAction = (msgIdx, actIdx) => {
    setMessages(m => m.map((mm, i) => i !== msgIdx ? mm : {
      ...mm,
      actions: mm.actions.map((aa, j) => j !== actIdx ? aa
        : { ok: false, cancelled: true, tool: aa.tool, entity: aa.entity, message: 'Cancelled.' }),
    }));
  };

  // Edit a previewed action — drops the original user prompt back into
  // the composer so they can refine and resend. Mantle's reply will be a
  // fresh preview. We also mark the old preview as cancelled to keep
  // the transcript honest.
  const editAction = (msgIdx, actIdx) => {
    const original = messages[msgIdx]?.userMessage || '';
    cancelAction(msgIdx, actIdx);
    setInput(original);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  // ---- VOICE INPUT (Whisper STT) -----------------------------------
  // Hold-to-record or tap-to-toggle mic → MediaRecorder → /ai/voice/transcribe.
  // Result drops into the composer so the user can review/edit before
  // sending — much better UX than auto-sending a misheard prompt.
  const startRecording = async () => {
    if (recording || transcribing) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Your browser doesn't support voice input.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick the first MIME the browser actually supports — Safari
      // hates webm, Chrome/Firefox prefer it.
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      const mime = candidates.find(m => window.MediaRecorder?.isTypeSupported?.(m)) || '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) recorderChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        // Stop all tracks immediately so the browser drops the mic
        // permission indicator — privacy hygiene.
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(recorderChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        recorderChunksRef.current = [];
        if (blob.size < 1500) {
          toast.error("Recording too short — try again.");
          return;
        }
        await uploadVoice(blob, rec.mimeType);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error(e?.name === 'NotAllowedError'
        ? 'Microphone blocked. Allow mic access in your browser settings.'
        : `Couldn't start recording: ${e.message || 'unknown error'}`);
    }
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    setRecording(false);
  };

  const uploadVoice = async (blob, mime) => {
    setTranscribing(true);
    try {
      // Browsers don't always give the blob a filename — Whisper uses
      // the extension to detect the format, so we name it explicitly.
      const ext = (mime || '').includes('mp4') ? 'm4a'
                : (mime || '').includes('ogg') ? 'ogg'
                : (mime || '').includes('wav') ? 'wav'
                : 'webm';
      const fd = new FormData();
      fd.append('audio', blob, `voice.${ext}`);
      const { data } = await api.post('/ai/voice/transcribe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const text = (data?.text || '').trim();
      if (!text) { toast.error("Couldn't hear that — try again."); return; }
      // Drop transcript into the composer — append if there's already
      // typed text, replace if the input is empty.
      setInput(prev => prev.trim() ? `${prev.trim()} ${text}` : text);
      setTimeout(() => inputRef.current?.focus(), 30);
    } catch (e) {
      toast.error(errMsg(e, 'Voice input failed.'));
    } finally {
      setTranscribing(false);
    }
  };

  // ---- CHAT MODE (SSE streaming) -----------------------------------
  const sendChat = async (text) => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
      body: JSON.stringify({ session_id: activeSession, message: text }),
      signal: ctrl.signal,
    });
    if (!resp.ok || !resp.body) {
      const detail = await resp.text();
      throw new Error(detail || `HTTP ${resp.status}`);
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let assistantText = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';
      for (const chunk of chunks) {
        const lines = chunk.split('\n');
        let evType = 'message';
        let dataLine = '';
        for (const ln of lines) {
          if (ln.startsWith('event:')) evType = ln.slice(6).trim();
          else if (ln.startsWith('data:')) dataLine += ln.slice(5).trim();
        }
        if (!dataLine) continue;
        if (evType === 'session') {
          try { setActiveSession(JSON.parse(dataLine).session_id); } catch (_) { /* ignore */ }
        } else if (evType === 'delta') {
          try {
            const d = JSON.parse(dataLine);
            if (d.delta) {
              assistantText += d.delta;
              setStreamingText(assistantText);
            }
          } catch (_) { /* ignore */ }
        } else if (evType === 'error') {
          try { toast.error(JSON.parse(dataLine).message || 'AI error'); } catch { toast.error('AI error'); }
        } else if (evType === 'done') {
          if (assistantText) setMessages(m => [...m, { role: 'assistant', content: assistantText }]);
          setStreamingText('');
        }
      }
    }
    api.get('/ai/sessions').then(r => setSessions(r.data || [])).catch(() => {});
  };

  const send = async (override) => {
    const text = (typeof override === 'string' ? override : input).trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    setMessages(m => [...m, { role: 'user', content: text }]);
    setStreamingText('');
    const slowTimer = setTimeout(() => setSlow(true), 15000);
    try {
      if (mode === 'agent') await sendAgent(text);
      else await sendChat(text);
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error(e.message?.includes('feature') ? 'AI is a Plus+ feature.' : 'Something went wrong. Please retry.');
      }
      setStreamingText('');
    } finally {
      clearTimeout(slowTimer);
      setSlow(false);
      setSending(false);
      abortRef.current = null;
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const onActionClick = (action) => {
    if (!action?.link) return;
    // Defensive: strip the legacy '/app/<resource>' prefix that earlier
    // tool-call versions emitted. The current backend returns clean
    // routes ('/files', '/estimates/<id>', etc.) — this just protects
    // against stale session-history rows from before the fix.
    let link = action.link;
    const legacyPrefix = /^\/app\/(clients|envoice|document|books|schedule|tasks)(\?|\/|$)/;
    if (legacyPrefix.test(link)) {
      link = link
        .replace(/^\/app\/clients/, '/clients')
        .replace(/^\/app\/envoice/, '/envoice')
        .replace(/^\/app\/document/, '/files?tab=documents&')
        .replace(/^\/app\/books/, '/bookkeeping')
        .replace(/^\/app\/schedule/, '/schedule')
        .replace(/^\/app\/tasks/, '/app')
        // Some of the above introduce '?&' if a query was already present —
        // collapse it back.
        .replace(/\?&/, '?');
    }
    onClose?.();
    navigate(link);
  };

  const contextSuggestions = context && context.editable ? CONTEXT_SUGGESTIONS[context.type] : null;
  const suggestions = contextSuggestions || (mode === 'agent' ? AGENT_SUGGESTIONS : CHAT_SUGGESTIONS);
  const disabledByPref = enabled && !enabled('ask_mantle');
  const ContextIcon = (context && CONTEXT_ICONS[context.type]) || Sparkles;

  return (
    <>
      <div
        data-testid="ask-mantle-backdrop"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />

      <aside
        data-testid="ask-mantle-panel"
        className={`fixed top-0 right-0 z-50 h-screen w-full sm:w-[440px] bg-gradient-to-b from-[#0E1620] to-[#121A25] border-l border-white/[0.06] shadow-2xl flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        // Full-screen panel on mobile — must clear the iOS status bar.
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#B98A2C] to-[#CBA33B] flex items-center justify-center shrink-0">
              <Sparkles size={14} className="text-[#121A25]" strokeWidth={2.6} />
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Ask Mantle</p>
              <p className="text-[9px] text-[#758A9F] -mt-0.5 truncate">
                {mode === 'agent'
                  ? (previewMode ? 'Agent · preview-only, you confirm each action' : 'Agent · can take real actions')
                  : 'Chat · answers only'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              data-testid="ask-mantle-mode-toggle"
              onClick={() => setMode(m => m === 'agent' ? 'chat' : 'agent')}
              title={mode === 'agent' ? 'Switch to chat-only' : 'Switch to agent (can take actions)'}
              className={`group flex items-center gap-1 px-2 h-8 rounded-md text-[10px] font-semibold transition-all ${
                mode === 'agent'
                  ? 'bg-[#B98A2C]/15 text-[#CBA33B] hover:bg-[#B98A2C]/25'
                  : 'bg-white/[0.04] text-[#A8B8CA] hover:bg-white/[0.08]'
              }`}
            >
              {mode === 'agent' ? <Zap size={11} strokeWidth={2.6} /> : <Bot size={11} strokeWidth={2.6} />}
              {mode === 'agent' ? 'AGENT' : 'CHAT'}
            </button>
            {mode === 'agent' && (
              <button
                data-testid="ask-mantle-preview-toggle"
                onClick={() => setPreviewMode(v => !v)}
                title={previewMode
                  ? 'Preview mode ON — actions need confirmation. Click to turn off.'
                  : 'Preview mode OFF — actions run immediately. Click to require confirmation (safe for live demos).'}
                className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                  previewMode
                    ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25'
                    : 'hover:bg-white/[0.04] text-[#758A9F]'
                }`}
              >
                <Eye size={15} strokeWidth={previewMode ? 2.6 : 2} />
              </button>
            )}
            <button data-testid="ask-mantle-help-btn" onClick={() => { setShowHelp(v => !v); setShowHistory(false); }} title="What can Mantle do?"
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${showHelp ? 'bg-[#B98A2C]/15 text-[#CBA33B]' : 'hover:bg-white/[0.04] text-[#A8B8CA]'}`}>
              <HelpCircle size={15} />
            </button>
            <button data-testid="ask-mantle-history-btn" onClick={() => { setShowHistory(v => !v); setShowHelp(false); }} title="History"
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${showHistory ? 'bg-[#B98A2C]/15 text-[#CBA33B]' : 'hover:bg-white/[0.04] text-[#A8B8CA]'}`}>
              <MessageSquare size={15} />
            </button>
            <button data-testid="ask-mantle-new-btn" onClick={newChat} title="New chat"
              className="w-8 h-8 rounded-md hover:bg-white/[0.04] flex items-center justify-center text-[#A8B8CA]">
              <Plus size={16} />
            </button>
            <button data-testid="ask-mantle-close-btn" onClick={onClose} title="Close"
              className="w-8 h-8 rounded-md hover:bg-white/[0.04] flex items-center justify-center text-[#A8B8CA]">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Context chip — what Mantle is currently looking at */}
        {context && (
          <div data-testid="ask-mantle-context-chip"
            className={`flex items-center gap-2 px-4 py-2 border-b border-white/[0.06] ${context.editable ? 'bg-[#B98A2C]/10' : 'bg-white/[0.02]'}`}>
            <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${context.editable ? 'bg-[#B98A2C]/20 text-[#CBA33B]' : 'bg-white/[0.05] text-[#A8B8CA]'}`}>
              <ContextIcon size={12} strokeWidth={2.4} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#758A9F] leading-none">
                {context.editable ? 'Editing with you' : 'Looking at'}
              </p>
              <p className="text-[11.5px] text-white truncate leading-tight" data-testid="ask-mantle-context-label">{context.label}</p>
            </div>
            {context.editable && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 shrink-0">live edits</span>
            )}
          </div>
        )}

        {/* History drawer */}
        {showHistory && (
          <div data-testid="ask-mantle-history" className="border-b border-white/[0.06] bg-black/20 max-h-[280px] overflow-y-auto">
            {sessions.length === 0 ? (
              <p className="text-[11px] text-[#758A9F] px-4 py-3">No previous conversations.</p>
            ) : sessions.map(s => (
              <div key={s.session_id} onClick={() => loadSession(s.session_id)}
                className={`group flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-white/[0.03] ${activeSession === s.session_id ? 'bg-[#B98A2C]/8' : ''}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] text-white truncate">{s.title || 'Untitled'}</p>
                  <p className="text-[9px] text-[#758A9F]">{new Date(s.last_at).toLocaleDateString()} · {s.turns} turns</p>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-[#758A9F] hover:text-red-400 ml-2"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        )}

        {/* "What can Mantle do?" — full overlay drawer that hides the chat
            while open so the user can browse the tool catalog without
            losing place. Closing returns them to their conversation. */}
        {showHelp && (
          <div data-testid="ask-mantle-help" className="flex-1 overflow-y-auto bg-gradient-to-b from-[#0E1620] to-[#121A25]">
            <div className="px-4 pt-4 pb-3 border-b border-white/[0.06] flex items-center gap-2">
              <button
                data-testid="ask-mantle-help-back"
                onClick={() => setShowHelp(false)}
                className="w-7 h-7 rounded-md hover:bg-white/[0.06] flex items-center justify-center text-[#A8B8CA]"
                title="Back to chat"
              >
                <ArrowLeft size={14} />
              </button>
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>What can Ask Mantle do?</p>
                <p className="text-[10px] text-[#758A9F] -mt-0.5">{TOOL_HELP.length} actions · tap an example to try it</p>
              </div>
            </div>
            <div className="px-3 py-3 space-y-2.5">
              {TOOL_HELP.map(t => {
                const Icon = t.icon;
                return (
                  <div key={t.tool} data-testid={`help-tool-${t.tool}`} className="rounded-xl bg-white/[0.03] border border-white/[0.05] overflow-hidden">
                    <div className="flex items-start gap-3 px-3.5 py-3 border-b border-white/[0.04]">
                      <div className="w-8 h-8 rounded-lg bg-[#B98A2C]/15 flex items-center justify-center shrink-0">
                        <Icon size={15} className="text-[#CBA33B]" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[12.5px] font-bold text-white tracking-tight">{t.label}</p>
                        <p className="text-[11.5px] text-[#A8B8CA] leading-relaxed mt-0.5">{t.summary}</p>
                      </div>
                    </div>
                    <div className="px-3 py-2 space-y-1.5">
                      {t.examples.map((ex, i) => (
                        <button
                          key={i}
                          data-testid={`help-example-${t.tool}-${i}`}
                          onClick={() => {
                            setInput(ex);
                            setShowHelp(false);
                            setMode('agent');
                            setTimeout(() => inputRef.current?.focus(), 50);
                          }}
                          className="w-full text-left px-2.5 py-1.5 rounded-md bg-black/20 hover:bg-[#B98A2C]/12 text-[11.5px] text-[#D6E0EC] hover:text-white transition-colors leading-snug"
                        >
                          <span className="text-[#B98A2C] mr-1">›</span> {ex}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              <p className="text-[10.5px] text-[#758A9F] text-center pt-2 pb-1">
                Just describe what you want in plain English — Mantle figures out which action to run.
              </p>
            </div>
          </div>
        )}

        {/* Conversation */}
        {!showHelp && (
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.length === 0 && !streamingText && (
            <div className="text-center pt-10">
              <div className="inline-flex w-12 h-12 rounded-full bg-gradient-to-br from-[#B98A2C]/30 to-[#CBA33B]/15 items-center justify-center mb-3">
                <Sparkles size={22} className="text-[#CBA33B]" />
              </div>
              <p className="text-[14px] font-bold text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {contextSuggestions ? `Let's refine this ${context.type}.` : mode === 'agent' ? 'Tell me what to do.' : 'How can I help?'}
              </p>
              <p className="text-[11px] text-[#758A9F] mb-5 px-6">
                {contextSuggestions
                  ? 'Ask for targeted changes — I edit what you have open instead of starting over. Say "undo that" to revert.'
                  : mode === 'agent'
                  ? "I can add clients, draft estimates / invoices / contracts, log expenses, schedule events, and add tasks — just describe it."
                  : "Ask about your invoices, draft an email to a client, get a recap of your week."}
              </p>
              <div className="space-y-2 px-4">
                {suggestions.map(s => (
                  <button
                    key={s}
                    data-testid={`ask-suggestion-${s.substring(0, 20).replace(/[^a-z0-9]/gi, '-')}`}
                    onClick={() => send(s)}
                    className="block w-full text-left px-3 py-2 text-[12px] rounded-md bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.04] text-[#A8B8CA] hover:text-white transition-all">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <React.Fragment key={i}>
              <MessageBubble role={m.role} content={m.content} />
              {m.role === 'assistant' && Array.isArray(m.actions) && m.actions.length > 0 && (
                <div className="space-y-2">
                  {m.actions.map((a, j) => (
                    <ActionCard
                      key={j}
                      action={a}
                      onOpen={() => onActionClick(a)}
                      onConfirm={() => confirmAction(i, j)}
                      onCancel={() => cancelAction(i, j)}
                      onEdit={() => editAction(i, j)}
                      confirming={confirmingIdx === `${i}-${j}`}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
          {streamingText && <MessageBubble role="assistant" content={streamingText} streaming />}
          {sending && !streamingText && (
            <div className="flex items-center gap-2 text-[#758A9F] text-[11px] pl-1">
              <Loader2 size={12} className="animate-spin" /> {mode === 'agent' ? (slow ? 'Still working — bigger rewrites can take a minute or two…' : 'Mantle is on it…') : 'Mantle is thinking…'}
            </div>
          )}
        </div>
        )}

        {/* Composer */}
        <div className="border-t border-white/[0.06] px-3 py-3 bg-black/20">
          {disabledByPref ? (
            <div className="text-center py-3">
              <p className="text-[11px] text-[#758A9F]">Ask Mantle is turned off in Settings → AI.</p>
              <button onClick={() => { onClose?.(); navigate('/settings?section=ai'); }}
                className="text-[11px] text-[#CBA33B] hover:underline mt-1">Open settings →</button>
            </div>
          ) : (
            <>
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="ask-mantle-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={recording ? 'Recording… tap the red button to stop' : (contextSuggestions ? `Ask for a change to this ${context.type}…` : mode === 'agent' ? 'Try: "Make me a flooring estimate for…"' : 'Ask anything about your business…')}
                  rows={1}
                  className="flex-1 resize-none bg-white/[0.04] border border-white/[0.08] focus:border-[#B98A2C]/50 rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-[#758A9F] outline-none overflow-y-auto leading-snug"
                  style={{ maxHeight: '220px' }}
                  disabled={recording || transcribing}
                />
                {/* Voice input — tap to start, tap again to stop */}
                <button
                  data-testid={`ask-mantle-mic-btn${recording ? '-recording' : ''}`}
                  onClick={recording ? stopRecording : startRecording}
                  disabled={transcribing || sending}
                  title={recording ? 'Stop recording' : 'Voice input (Whisper)'}
                  className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                    recording
                      ? 'bg-red-500 hover:bg-red-400 text-white animate-pulse shadow-[0_0_20px_rgba(239,68,68,0.5)]'
                      : transcribing
                        ? 'bg-white/[0.04] text-[#CBA33B]'
                        : 'bg-white/[0.04] hover:bg-white/[0.08] text-[#A8B8CA] hover:text-white'
                  }`}
                >
                  {transcribing ? <Loader2 size={16} className="animate-spin" />
                    : recording ? <Square size={14} strokeWidth={2.6} fill="currentColor" />
                    : <Mic size={16} strokeWidth={2.2} />}
                </button>
                <button data-testid="ask-mantle-send-btn" onClick={() => send()} disabled={!input.trim() || sending || recording || transcribing}
                  className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-[#B98A2C] to-[#CBA33B] text-[#121A25] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:scale-[1.04] active:scale-95 transition-all">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2.4} />}
                </button>
              </div>
              <p className="text-[9px] text-[#758A9F] mt-1.5 px-1">
                {mode === 'agent'
                  ? (previewMode
                      ? 'Preview mode is ON — actions only run after you tap Confirm. Safe to demo live.'
                      : 'Agent will execute actions you describe. Review the results before sending to clients.')
                  : 'AI can make mistakes. Double-check numbers before sending to clients.'}
              </p>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function MessageBubble({ role, content, streaming }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
        isUser
          ? 'bg-gradient-to-br from-[#B98A2C] to-[#CBA33B] text-[#121A25] font-medium rounded-br-sm'
          : 'bg-white/[0.04] border border-white/[0.04] text-[#D6E0EC] rounded-bl-sm'
      }`}>
        {content}
        {streaming && <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-[#CBA33B] animate-pulse" />}
      </div>
    </div>
  );
}

function ActionCard({ action, onOpen, onConfirm, onCancel, onEdit, confirming }) {
  const Icon = ACTION_ICONS[action?.entity?.type] || Sparkles;
  const isPreview = !!action.preview;
  const isCancelled = !!action.cancelled;
  const isLocal = !!action.client_apply;
  const ok = !!action.ok && !isCancelled;
  const tone = isCancelled
    ? 'bg-white/[0.02] border-white/[0.06]'
    : isPreview
      ? 'bg-emerald-500/8 border-emerald-500/25 hover:border-emerald-500/45'
      : ok
        ? 'bg-[#B98A2C]/8 border-[#B98A2C]/25 hover:border-[#B98A2C]/45'
        : 'bg-red-500/8 border-red-500/30';
  const iconTone = isCancelled
    ? 'bg-white/[0.04] text-[#758A9F]'
    : isPreview
      ? 'bg-emerald-500/15 text-emerald-300'
      : ok
        ? 'bg-[#B98A2C]/15 text-[#CBA33B]'
        : 'bg-red-500/15 text-red-300';
  const okLabel = isLocal
    ? (action.applied === false ? 'Could not apply' : action.entity?.type === 'undo' ? 'Reverted in editor' : 'Applied in editor')
    : (action.entity?.type || action.tool || '').replace('_', ' ');

  return (
    <div data-testid={`ai-action-${action.tool}${isPreview ? '-preview' : ''}`}
      className={`rounded-xl border ${tone} transition-colors`}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconTone}`}>
          {isPreview ? <Eye size={16} strokeWidth={2} />
            : ok ? <Icon size={16} strokeWidth={2} />
            : <AlertCircle size={16} strokeWidth={2} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#758A9F]">
            {isCancelled ? (
              <span className="text-[#758A9F]">Cancelled · {(action.entity?.type || action.tool || '').replace('_', ' ')}</span>
            ) : isPreview ? (
              <span className="inline-flex items-center gap-1 text-emerald-300"><Eye size={10} /> Preview · {isLocal ? 'edit' : (action.entity?.type || action.tool || '').replace('_', ' ')}</span>
            ) : ok ? (
              <span className="inline-flex items-center gap-1 text-[#CBA33B]"><CheckCircle2 size={10} /> {okLabel}</span>
            ) : (
              <span className="text-red-300">Couldn&apos;t do that</span>
            )}
          </p>
          <p className="text-[12.5px] text-white truncate">{action.entity?.label || action.message}</p>
          {isPreview && action.message && (
            <p className="text-[10.5px] text-emerald-200/80 mt-0.5 line-clamp-2">{action.message}</p>
          )}
          {ok && action.entity?.total != null && (
            <p className="text-[10px] text-[#A8B8CA]">${Number(action.entity.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          )}
          {!ok && !isCancelled && !isPreview && (
            <p className="text-[10.5px] text-red-300/85 mt-0.5 line-clamp-2">{action.message}</p>
          )}
        </div>
        {ok && !isPreview && action.link && (
          <button data-testid={`ai-action-open-${action.tool}`} onClick={onOpen}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 h-7 text-[11px] font-semibold rounded-md bg-[#B98A2C]/20 text-[#CBA33B] hover:bg-[#B98A2C]/30 hover:text-white transition-colors">
            Open <ExternalLink size={11} />
          </button>
        )}
      </div>
      {isPreview && (
        <div className="flex items-center gap-1.5 px-3 pb-2.5 pt-0.5 border-t border-white/[0.04]">
          <button
            data-testid={`ai-action-confirm-${action.tool}`}
            onClick={onConfirm}
            disabled={confirming}
            className="flex-1 inline-flex items-center justify-center gap-1 h-8 px-2 text-[11px] font-bold rounded-md bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {confirming ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={2.8} />}
            {confirming ? 'Running…' : 'Confirm'}
          </button>
          <button
            data-testid={`ai-action-edit-${action.tool}`}
            onClick={onEdit}
            disabled={confirming}
            title="Refine your request and re-send"
            className="inline-flex items-center justify-center gap-1 h-8 px-2.5 text-[11px] font-semibold rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-[#D6E0EC] transition-colors"
          >
            <Pencil size={11} /> Edit
          </button>
          <button
            data-testid={`ai-action-cancel-${action.tool}`}
            onClick={onCancel}
            disabled={confirming}
            className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/[0.04] hover:bg-red-500/15 text-[#758A9F] hover:text-red-300 transition-colors"
            title="Cancel — don't run this action"
          >
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
